// ============================================================
// Primitive 2 — NumberField.
// ------------------------------------------------------------
// LE composant le plus important de l'application. Celui d'AGC
// (`NumField` dans shell.jsx) est marqué NE PAS REPRENDRE : il
// rendait impossible la saisie de « 28,5 ».
//
// La cause : il convertissait la saisie en nombre à CHAQUE frappe.
// Taper « 28, » donnait `parseFloat("28,") === 28`, le champ se
// réécrivait en « 28 », et la virgule disparaissait sous les doigts.
// Vider le champ donnait `0`, impossible à effacer.
//
// La correction, et elle est simple : l'état local est une CHAÎNE.
// On ne convertit qu'au blur ou à Entrée. Le champ vide reste vide et
// vaut `null` — pas zéro. Une entrée manquante ne vaut jamais zéro,
// c'est la règle du moteur de prix, elle commence ici.
// ============================================================

import { useEffect, useId, useRef, useState } from 'react';
import SourceDot from './SourceDot.jsx';

/** Accepte la virgule décimale française et les espaces de milliers. */
function parse(text) {
  const cleaned = String(text).replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Affichage avec virgule : on saisit et on relit dans la même langue. */
function display(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).replace('.', ',');
}

export function NumberField({
  // Le label reste dans le DOM pour les lecteurs d'écran ; seul son
  // affichage est masqué. Un champ sans label accessible est
  // inutilisable au clavier.
  hideLabel = false,
  label,
  value,
  onChange,
  // Appelé au SEUL commit (Entrée, perte du focus) — pour une valeur qui
  // ne doit partir qu'une fois tapée en entier. Voir `CotesLues`.
  onCommit,
  unit,
  hint,
  error,
  source,
  min,
  max,
  disabled = false,
  required = false,
  id: providedId,
  ...props
}) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const [text, setText] = useState(() => display(value));
  /** Le champ a-t-il le focus ? Voir l'effet ci-dessous. */
  const focus = useRef(false);

  // Une valeur qui change à l'extérieur (application d'une proposition
  // de l'agent 2) se reflète ici — mais JAMAIS pendant qu'on tape.
  //
  // Depuis que la frappe remonte la valeur (voir `saisir`), le parent
  // renvoie aussitôt ce qu'on vient de lui donner. Sans ce garde, le
  // texte serait réécrit à chaque touche et « 28, » redeviendrait
  // « 28 » sous les doigts — le défaut d'AGC, par un autre chemin.
  useEffect(() => {
    if (focus.current) return;
    if (parse(text) !== value) setText(display(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /** Dans les bornes ? Le recadrage, lui, n'a lieu qu'au commit. */
  const dansLesBornes = (n) =>
    (min === undefined || n >= min) && (max === undefined || n <= max);

  // ══════════════════════════════════════════════════════════════
  // LA FRAPPE REMONTE LA VALEUR — réunion du 1er septembre 2026.
  // ──────────────────────────────────────────────────────────────
  // Charles : « on modifie un temps, le prix ne bouge pas tant qu'on ne
  // clique pas sur Enregistrer, et pas systématiquement. »
  //
  // Reproduit dans un vrai navigateur sur le devis 2026-08-120 : à la
  // frappe, le prix reste à 47,50 € ; au blur, il tombe à 47,00 €. La
  // valeur ne remontait qu'au `commit` — donc au blur ou à Entrée. Le
  // prix ne suivait pas la gamme, il suivait le FOCUS. Et Raph ne l'a
  // jamais vu parce qu'il quitte le champ avant de regarder.
  //
  // Ce qu'on ne casse pas en le corrigeant : on remonte la valeur
  // ANALYSÉE sans jamais réécrire le texte affiché. C'est la réécriture
  // qui faisait disparaître la virgule chez AGC, pas la conversion.
  // Taper « 28, » remonte donc 28 et laisse « 28, » à l'écran.
  //
  // Le recadrage min/max reste au commit : le faire à la frappe
  // empêcherait de taper « 50 » dans un champ dont le minimum est 10 —
  // le « 5 » sauterait à 10 avant le second chiffre.
  // ══════════════════════════════════════════════════════════════
  function saisir(brut) {
    setText(brut);
    const parsed = parse(brut);
    if (parsed === null) {
      onChange?.(null);
      return;
    }
    if (dansLesBornes(parsed)) onChange?.(parsed);
  }

  function commit() {
    const parsed = parse(text);
    if (parsed === null) {
      setText('');
      onChange?.(null);
      onCommit?.(null);
      return;
    }
    let next = parsed;
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    setText(display(next));
    onChange?.(next);
    onCommit?.(next);
  }

  return (
    <div className="field">
      <label className={hideLabel ? "field__label sr-only" : "field__label"} htmlFor={id}>
        {source ? <SourceDot source={source} /> : null}
        {label}
        {required ? <span aria-hidden="true">*</span> : null}
      </label>
      <div className={`nf${unit ? ' nf--with-unit' : ''}`}>
        <input
          id={id}
          className="input"
          // `inputMode="decimal"` et non `type="number"` : le type
          // number refuse la virgule dans plusieurs navigateurs et
          // ajoute des flèches qui modifient la valeur par erreur.
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={text}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          onChange={(e) => saisir(e.target.value)}
          onFocus={() => { focus.current = true; }}
          onBlur={() => { focus.current = false; commit(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          {...props}
        />
        {unit ? (
          <span className="nf__unit" aria-hidden="true">
            {unit}
          </span>
        ) : null}
      </div>
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          {error}
        </span>
      ) : hint ? (
        <span className="field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export default NumberField;
